class SodiiSocialCarousel extends HTMLElement {
  #track;
  #cards = [];
  #previousButton;
  #nextButton;
  #status;
  #progress;
  #shareStatus;
  #activeIndex = 0;
  #animationFrame;
  #dragStartX = 0;
  #dragStartScrollLeft = 0;
  #dragged = false;
  #videoObserver;

  connectedCallback() {
    this.#track = this.querySelector('[data-social-track]');
    if (!this.#track) return;

    this.#cards = [...this.querySelectorAll('[data-social-card]')];
    this.#previousButton = this.querySelector('[data-social-previous]');
    this.#nextButton = this.querySelector('[data-social-next]');
    this.#status = this.querySelector('[data-social-status]');
    this.#progress = this.querySelector('[data-social-progress]');
    this.#shareStatus = this.querySelector('[data-share-status]');

    this.#track.addEventListener('scroll', this.#handleScroll, { passive: true });
    this.#track.addEventListener('keydown', this.#handleKeydown);
    this.#track.addEventListener('pointerdown', this.#handlePointerDown);
    this.#track.addEventListener('pointermove', this.#handlePointerMove);
    this.#track.addEventListener('pointerup', this.#finishDrag);
    this.#track.addEventListener('pointercancel', this.#finishDrag);
    this.#track.addEventListener('click', this.#preventDraggedClick, true);
    this.addEventListener('click', this.#handleClick);
    this.addEventListener('dblclick', this.#handleDoubleClick);
    this.addEventListener('shopify:block:select', this.#handleBlockSelect);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);
    window.addEventListener('resize', this.#scheduleUpdate, { passive: true });

    this.#bindVideos();
    this.#observeVideos();
    this.#updateCarousel();
  }

  disconnectedCallback() {
    cancelAnimationFrame(this.#animationFrame);
    this.#videoObserver?.disconnect();
    this.#track?.removeEventListener('scroll', this.#handleScroll);
    this.#track?.removeEventListener('keydown', this.#handleKeydown);
    this.#track?.removeEventListener('pointerdown', this.#handlePointerDown);
    this.#track?.removeEventListener('pointermove', this.#handlePointerMove);
    this.#track?.removeEventListener('pointerup', this.#finishDrag);
    this.#track?.removeEventListener('pointercancel', this.#finishDrag);
    this.#track?.removeEventListener('click', this.#preventDraggedClick, true);
    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('dblclick', this.#handleDoubleClick);
    this.removeEventListener('shopify:block:select', this.#handleBlockSelect);
    document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
    window.removeEventListener('resize', this.#scheduleUpdate);

    this.querySelectorAll('video').forEach((video) => {
      video.removeEventListener('play', this.#handleVideoStateChange);
      video.removeEventListener('pause', this.#handleVideoStateChange);
      video.removeEventListener('volumechange', this.#handleVideoStateChange);
    });
  }

  #handleScroll = () => this.#scheduleUpdate();

  #scheduleUpdate = () => {
    cancelAnimationFrame(this.#animationFrame);
    this.#animationFrame = requestAnimationFrame(this.#updateCarousel);
  };

  #updateCarousel = () => {
    if (!this.#track || !this.#cards.length) return;

    const trackBounds = this.#track.getBoundingClientRect();
    const startEdge = trackBounds.left + (Number.parseFloat(getComputedStyle(this.#track).paddingLeft) || 0);
    let nearestDistance = Number.POSITIVE_INFINITY;

    this.#cards.forEach((card, index) => {
      const distance = Math.abs(card.getBoundingClientRect().left - startEdge);
      if (distance < nearestDistance) {
        nearestDistance = distance;
        this.#activeIndex = index;
      }
    });
    this.#cards.forEach((card, index) => card.toggleAttribute('aria-current', index === this.#activeIndex));

    const maxScroll = Math.max(0, this.#track.scrollWidth - this.#track.clientWidth);
    const atStart = this.#track.scrollLeft <= 2 || this.#activeIndex === 0;
    const atEnd = this.#track.scrollLeft >= maxScroll - 2 || this.#activeIndex === this.#cards.length - 1;

    if (this.#previousButton) this.#previousButton.disabled = atStart;
    if (this.#nextButton) this.#nextButton.disabled = atEnd;
    if (this.#status) this.#status.textContent = `${this.#activeIndex + 1} / ${this.#cards.length}`;
    if (this.#progress) {
      this.#progress.style.transform = `scaleX(${(this.#activeIndex + 1) / this.#cards.length})`;
    }
  };

  #scrollToCard(index) {
    const nextIndex = Math.max(0, Math.min(index, this.#cards.length - 1));
    const card = this.#cards[nextIndex];
    if (!card) return;

    const padding = Number.parseFloat(getComputedStyle(this.#track).paddingLeft) || 0;
    this.#track.scrollTo({
      left: card.offsetLeft - padding,
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
    });
  }

  #handleKeydown = (event) => {
    let nextIndex;

    if (event.key === 'ArrowLeft') nextIndex = this.#activeIndex - 1;
    if (event.key === 'ArrowRight') nextIndex = this.#activeIndex + 1;
    if (event.key === 'Home') nextIndex = 0;
    if (event.key === 'End') nextIndex = this.#cards.length - 1;
    if (nextIndex === undefined) return;

    event.preventDefault();
    this.#scrollToCard(nextIndex);
  };

  #handlePointerDown = (event) => {
    if (event.pointerType !== 'mouse' || event.button !== 0) return;
    if (event.target.closest('a, button, input, select, textarea')) return;

    this.#dragStartX = event.clientX;
    this.#dragStartScrollLeft = this.#track.scrollLeft;
    this.#dragged = false;
    this.#track.classList.add('is-dragging');
    this.#track.setPointerCapture(event.pointerId);
  };

  #handlePointerMove = (event) => {
    if (!this.#track.classList.contains('is-dragging')) return;

    const distance = event.clientX - this.#dragStartX;
    if (Math.abs(distance) > 4) this.#dragged = true;
    this.#track.scrollLeft = this.#dragStartScrollLeft - distance;
  };

  #finishDrag = (event) => {
    if (!this.#track.classList.contains('is-dragging')) return;

    this.#track.classList.remove('is-dragging');
    if (this.#track.hasPointerCapture(event.pointerId)) this.#track.releasePointerCapture(event.pointerId);
    this.#updateCarousel();
    this.#scrollToCard(this.#activeIndex);
  };

  #preventDraggedClick = (event) => {
    if (!this.#dragged) return;
    event.preventDefault();
    event.stopPropagation();
    this.#dragged = false;
  };

  #handleClick = (event) => {
    if (event.target.closest('[data-social-previous]')) {
      this.#scrollToCard(this.#activeIndex - 1);
      return;
    }

    if (event.target.closest('[data-social-next]')) {
      this.#scrollToCard(this.#activeIndex + 1);
      return;
    }

    const likeButton = event.target.closest('[data-like-toggle]');
    if (likeButton) {
      this.#togglePressed(likeButton, 'Like post', 'Unlike post');
      return;
    }

    const saveButton = event.target.closest('[data-save-toggle]');
    if (saveButton) {
      this.#togglePressed(saveButton, 'Save post', 'Remove saved post');
      return;
    }

    const shareButton = event.target.closest('[data-share-post]');
    if (shareButton) {
      this.#sharePost(shareButton);
      return;
    }

    const captionButton = event.target.closest('[data-caption-toggle]');
    if (captionButton) {
      this.#toggleCaption(captionButton);
      return;
    }

    const playButton = event.target.closest('[data-video-toggle]');
    if (playButton) {
      this.#toggleVideo(playButton);
      return;
    }

    const soundButton = event.target.closest('[data-sound-toggle]');
    if (soundButton) this.#toggleSound(soundButton);
  };

  #handleDoubleClick = (event) => {
    const media = event.target.closest('[data-social-media]');
    if (!media || event.target.closest('button, a')) return;

    const card = media.closest('[data-social-card]');
    const likeButton = card?.querySelector('[data-like-toggle]');
    if (likeButton && likeButton.getAttribute('aria-pressed') !== 'true') {
      this.#togglePressed(likeButton, 'Like post', 'Unlike post');
    }

    const burst = card?.querySelector('.sodii-social-card__heart-burst');
    if (!burst || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    burst.classList.remove('is-active');
    requestAnimationFrame(() => burst.classList.add('is-active'));
  };

  #togglePressed(button, inactiveLabel, activeLabel) {
    const isPressed = button.getAttribute('aria-pressed') === 'true';
    button.setAttribute('aria-pressed', (!isPressed).toString());
    button.setAttribute('aria-label', isPressed ? inactiveLabel : activeLabel);
  }

  #toggleCaption(button) {
    const caption = button.closest('.sodii-social-card__caption-wrap')?.querySelector('[data-caption]');
    if (!caption) return;

    const isExpanded = button.getAttribute('aria-expanded') === 'true';
    button.setAttribute('aria-expanded', (!isExpanded).toString());
    button.textContent = isExpanded ? 'more' : 'less';
    caption.classList.toggle('is-expanded', !isExpanded);
  }

  async #sharePost(button) {
    const url = button.dataset.shareUrl;
    const title = button.dataset.shareTitle || document.title;
    if (!url) return;

    try {
      if (navigator.share) {
        await navigator.share({ title, url });
        this.#announce('Post shared.');
      } else if (navigator.clipboard) {
        await navigator.clipboard.writeText(url);
        this.#announce('Post link copied to clipboard.');
      } else {
        this.#announce('Open the post to copy its link.');
      }
    } catch (error) {
      if (error?.name !== 'AbortError') this.#announce('The post link could not be shared.');
    }
  }

  #announce(message) {
    if (!this.#shareStatus) return;
    this.#shareStatus.textContent = '';
    requestAnimationFrame(() => {
      this.#shareStatus.textContent = message;
    });
  }

  #bindVideos() {
    this.querySelectorAll('video').forEach((video) => {
      video.muted = true;
      video.addEventListener('play', this.#handleVideoStateChange);
      video.addEventListener('pause', this.#handleVideoStateChange);
      video.addEventListener('volumechange', this.#handleVideoStateChange);
      this.#syncVideoControls(video);
    });
  }

  #hydrateVideo(video) {
    const lazySources = video.querySelectorAll('source[data-src]');
    if (!lazySources.length) return;

    lazySources.forEach((source) => {
      source.src = source.dataset.src;
      source.removeAttribute('data-src');
    });
    video.load();
  }

  #toggleVideo(button) {
    const video = button.closest('[data-social-card]')?.querySelector('video');
    if (!video) return;

    this.#hydrateVideo(video);
    if (video.paused) {
      video.play().catch(() => this.#syncVideoControls(video));
    } else {
      video.pause();
    }
  }

  #toggleSound(button) {
    const video = button.closest('[data-social-card]')?.querySelector('video');
    if (!video) return;

    this.#hydrateVideo(video);
    const shouldUnmute = video.muted;
    if (shouldUnmute) {
      this.querySelectorAll('video').forEach((otherVideo) => {
        if (otherVideo === video) return;
        otherVideo.muted = true;
      });
    }

    video.muted = !shouldUnmute;
    video.play().catch(() => this.#syncVideoControls(video));
    this.#syncVideoControls(video);
  }

  #handleVideoStateChange = (event) => this.#syncVideoControls(event.currentTarget);

  #syncVideoControls(video) {
    const card = video.closest('[data-social-card]');
    const playButton = card?.querySelector('[data-video-toggle]');
    const soundButton = card?.querySelector('[data-sound-toggle]');

    if (playButton) {
      const isPlaying = !video.paused;
      playButton.setAttribute('aria-pressed', isPlaying.toString());
      playButton.setAttribute('aria-label', isPlaying ? 'Pause video' : 'Play video');
    }

    if (soundButton) {
      const isSoundOn = !video.muted;
      soundButton.setAttribute('aria-pressed', isSoundOn.toString());
      soundButton.setAttribute('aria-label', isSoundOn ? 'Turn sound off' : 'Turn sound on');
    }
  }

  #observeVideos() {
    const videos = [...this.querySelectorAll('video')];
    if (!videos.length || !('IntersectionObserver' in window)) return;

    const shouldAutoplay = this.dataset.autoplayVideos === 'true' &&
      !window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.#videoObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const video = entry.target;
          if (entry.isIntersecting && entry.intersectionRatio >= 0.55) {
            this.#hydrateVideo(video);
            if (shouldAutoplay) video.play().catch(() => this.#syncVideoControls(video));
            return;
          }

          video.pause();
          video.muted = true;
          this.#syncVideoControls(video);
        });
      },
      { root: this.#track, rootMargin: '0px 35%', threshold: [0, 0.55] }
    );

    videos.forEach((video) => this.#videoObserver.observe(video));
  }

  #handleVisibilityChange = () => {
    if (!document.hidden) return;
    this.querySelectorAll('video').forEach((video) => {
      video.pause();
      video.muted = true;
    });
  };

  #handleBlockSelect = (event) => {
    const card = event.target.closest?.('[data-social-card]');
    if (!card || !this.contains(card)) return;
    this.#scrollToCard(this.#cards.indexOf(card));
  };
}

if (!customElements.get('sodii-social-carousel')) {
  customElements.define('sodii-social-carousel', SodiiSocialCarousel);
}
